import React, { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { observer } from 'mobx-react-lite'
import s from './styles/MessageStudentInfo.module.scss'
import InitialAvatar from '../../shared/components/InitialAvatar/InitialAvatar'
import { ReactComponent as PlanetIcon } from '../../assets/icons/planet.svg'
import { ReactComponent as CapIcon } from '../../assets/icons/cap.svg'
import { ReactComponent as BagIcon } from '../../assets/icons/bag_empty.svg'
import { ReactComponent as DockPenIcon } from '../../assets/icons/dock_pen.svg'
// import { ReactComponent as ClockIcon } from '../../assets/icons/clock.svg'
import { ReactComponent as HeaderIcon } from '../../assets/icons/human-circle.svg'
import { ReactComponent as CrossIcon } from '../../assets/icons/cross.svg'
import { useMessageStudentInfo } from './hooks/useMessageStudentInfo'
import { studentsStore } from '../../pages/Students/model/studentsStore'
import { navigateToStudentProfile } from 'pages/Dashboard/components/TodaysPrioritySection/utils/priorityCardUi'
import AssignActivityModal from '../AssignActivityModal/AssignActivityModal'
// import BookSessionModal from '../BookSessionModal/BookSessionModal'
import LoaderImg from 'shared/components/LoaderImg/LoaderImg'

const UNIVERSITIES_PREVIEW_COUNT = 3

export const MessageStudentInfo = observer(
	({ studentId, isCollapsed = false, onCollapse }) => {
		const navigate = useNavigate()
		const {
			student,
			name,
			grade,
			profileScore,
			countryList,
			universitiesList,
			careersList,
			assignments,
			isLoading,
			isLoadingAssignments,
		} = useMessageStudentInfo({ studentId })

		const [isAssignActivityOpen, setIsAssignActivityOpen] = useState(false)
		// const [isBookSessionOpen, setIsBookSessionOpen] = useState(false)
		const [showAllUniversities, setShowAllUniversities] = useState(false)

		const openStudentProfile = useCallback(() => {
			navigateToStudentProfile(navigate, studentId || student?.id)
		}, [navigate, student?.id, studentId])

		const visibleUniversities = useMemo(() => {
			if (showAllUniversities) return universitiesList
			return universitiesList.slice(0, UNIVERSITIES_PREVIEW_COUNT)
		}, [showAllUniversities, universitiesList])

		const hasMoreUniversities =
			universitiesList.length > UNIVERSITIES_PREVIEW_COUNT

		const openAssignForStudent = useCallback(() => {
			if (!student) return
			studentsStore.clearSelection()
			studentsStore.toggleStudentSelection(student.id)
			setIsAssignActivityOpen(true)
		}, [student])

		// const openSessionForStudent = useCallback(() => {
		// 	if (!student) return
		// 	setIsBookSessionOpen(true)
		// }, [student])

		if (isLoading) {
			return (
				<div className={s.wrap}>
					<div className={s.loading}>Loading...</div>
				</div>
			)
		}

		if (!student) {
			return (
				<div className={s.wrap}>
					<div className={s.empty}>Student not found</div>
				</div>
			)
		}

		return (
			<>
				<div className={`${s.wrap} ${isCollapsed ? s.collapsed : ''}`}>
					<div className={s.header}>
						<div className={s.header__icon}>
							<HeaderIcon />
						</div>
						<span className={s.header__title}>Student Profile</span>
						{onCollapse && (
							<button
								type="button"
								className={s.header__close}
								onClick={onCollapse}
								aria-label="Collapse"
							>
								<CrossIcon />
							</button>
						)}
					</div>

					<div className={s.body}>
						<div className={s.profile}>
							<button
								type="button"
								className={s.profile__content}
								onClick={openStudentProfile}
							>
								<div className={s.profile__photo}>
									<InitialAvatar
										name={name}
										size={56}
										avatarKey={student?.avatarKey}
										avatarSource={student?.avatarSource}
										photoUrl={student?.photoUrl}
										studentId={student?.id}
										student={student}
									/>
								</div>
								<div className={s.profile__info}>
									<span className={s.profile__name}>{name}</span>
									<span className={s.profile__grade}>Grade {grade}</span>
								</div>
							</button>
							<div className={s.profile__score}>
								<div className={s.profile__scoreHeader}>
									<span className={s.profile__scoreLabel}>Readiness</span>
									<span className={s.profile__scoreValue}>{profileScore}%</span>
								</div>
								<div className={s.profile__scoreBar}>
									<div
										className={s.profile__scoreFill}
										style={{ maxWidth: `${profileScore}%` }}
									/>
								</div>
							</div>
						</div>

						<div className={s.snapshot}>
							<span className={s.snapshot__title}>Student Snapshot</span>
							<div className={s.snapshot__items}>
								<div className={s.preference}>
									<div className={s.preference__header}>
										<div className={s.preference__icon}>
											<PlanetIcon />
										</div>
										<span className={s.preference__title}>Country</span>
									</div>
									<div className={s.preference__list}>
										{countryList.length > 0 ? (
											countryList.map(
												({ FlagIcon, name: countryName }, index) => (
													<div key={index} className={s.tag}>
														{FlagIcon && (
															<div className={s.tag__icon}>
																<FlagIcon />
															</div>
														)}
														<span className={s.tag__text}>{countryName}</span>
													</div>
												),
											)
										) : (
											<span className={s.tag__empty}>
												No countries selected
											</span>
										)}
									</div>
								</div>

								<div className={s.preference}>
									<div className={s.preference__header}>
										<div className={s.preference__icon}>
											<BagIcon />
										</div>
										<span className={s.preference__title}>Career</span>
									</div>
									<div className={s.preference__list}>
										{careersList.length > 0 ? (
											careersList.map((career, index) => (
												<div key={index} className={s.tag}>
													<span className={s.tag__text}>{career}</span>
												</div>
											))
										) : (
											<span className={s.tag__empty}>No career selected</span>
										)}
									</div>
								</div>

								<div className={s.preference}>
									<div className={s.preference__header}>
										<div className={s.preference__icon}>
											<CapIcon />
										</div>
										<span className={s.preference__title}>Universities</span>
									</div>
									<div className={s.preference__list}>
										{universitiesList.length > 0 ? (
											visibleUniversities.map((university, index) => (
												<div key={index} className={s.tag}>
													<span className={s.tag__text}>{university}</span>
												</div>
											))
										) : (
											<span className={s.tag__empty}>
												No universities selected
											</span>
										)}
									</div>
									{hasMoreUniversities && (
										<button
											type="button"
											className={s.snapshot__toggle}
											onClick={() => setShowAllUniversities(value => !value)}
										>
											{showAllUniversities ? 'Show less' : 'Show more'}
										</button>
									)}
								</div>
							</div>
						</div>

						<div className={s.assignments}>
							<div className={s.assignments__header}>
								<span className={s.assignments__title}>
									On-Going Assignments
								</span>
								{assignments.length > 0 && (
									<span className={s.assignments__count}>
										{assignments.length}
									</span>
								)}
							</div>
							<div className={s.assignments__list}>
								{assignments.length > 0 ? (
									assignments.map((assignment, index) => (
										<div key={assignment.id || index} className={s.assignment}>
											<p className={s.assignment__title}>{assignment.title}</p>
											<div className={s.assignment__footer}>
												{assignment.daysLeftLabel && (
													<span
														className={`${s.assignment__deadline} ${
															assignment.isUrgent
																? s.assignment__deadline_urgent
																: ''
														}`}
													>
														{assignment.daysLeftLabel}
													</span>
												)}
												<div className={s.assignment__pills}>
													{assignment.isUrgent && (
														<span
															className={`${s.assignment__pill} ${s.assignment__pill_urgent}`}
														>
															Urgent
														</span>
													)}
													<span
														className={`${s.assignment__pill} ${
															assignment.status === 'in_progress'
																? s.assignment__pill_progress
																: s.assignment__pill_status
														}`}
													>
														{assignment.statusLabel}
													</span>
												</div>
											</div>
										</div>
									))
								) : isLoadingAssignments ? (
									<div className={`${s.assignment} ${s.loading}`}>
										<p className={s.assignment__title}>
											<LoaderImg />
										</p>
									</div>
								) : (
									<span className={s.assignment__empty}>No assignments</span>
								)}
							</div>
						</div>
					</div>

					<div className={s.actions}>
						<button
							className={`${s.action} ${s.action_primary}`}
							onClick={openAssignForStudent}
						>
							<span className={s.action__icon}>
								<DockPenIcon />
							</span>
							<span className={s.action__text}>Assign Activity</span>
						</button>
						{/* <button
							className={`${s.action} ${s.action_secondary}`}
							onClick={openSessionForStudent}
						>
							<span className={s.action__icon}>
								<ClockIcon />
							</span>
							<span className={s.action__text}>Book Session</span>
						</button> */}
					</div>
				</div>
				<AssignActivityModal
					isOpen={isAssignActivityOpen}
					onClose={() => setIsAssignActivityOpen(false)}
					selectedStudents={studentsStore.selectedStudents}
					activeGrade={studentsStore.activeGrade}
					onSetGrade={grade => studentsStore.setGrade(grade)}
					clearSelectionOnOpen={false}
				/>
				{/* <BookSessionModal
					isOpen={isBookSessionOpen}
					onClose={() => setIsBookSessionOpen(false)}
					selectedStudents={student ? [student] : []}
				/> */}
			</>
		)
	},
)
